'use client';

import { useState } from 'react';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import Link from 'next/link';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  phase: string;
  weight: number;
}

interface Section {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'preparation',
    title: 'Preparation',
    description: 'Building capabilities before incidents occur',
    questions: [
      { id: 'pr1', text: 'Do you have a documented Incident Response Plan?', phase: 'Planning', weight: 10 },
      { id: 'pr2', text: 'Is there a dedicated Incident Response Team (IRT) with defined roles?', phase: 'Team', weight: 10 },
      { id: 'pr3', text: 'Are IRT members trained on incident response procedures?', phase: 'Training', weight: 9 },
      { id: 'pr4', text: 'Do you have 24/7 security monitoring capabilities?', phase: 'Monitoring', weight: 9 },
      { id: 'pr5', text: 'Are incident response tools and resources readily available?', phase: 'Tools', weight: 8 },
      { id: 'pr6', text: 'Is there a SIEM or centralized log management system?', phase: 'Technology', weight: 9 },
      { id: 'pr7', text: 'Are there pre-approved communication templates for incidents?', phase: 'Communication', weight: 7 },
      { id: 'pr8', text: 'Do you maintain relationships with external IR support (retainers)?', phase: 'External', weight: 7 },
    ],
  },
  {
    id: 'detection',
    title: 'Detection & Analysis',
    description: 'Identifying and understanding security incidents',
    questions: [
      { id: 'dt1', text: 'Are security alerts monitored and triaged in a timely manner?', phase: 'Monitoring', weight: 10 },
      { id: 'dt2', text: 'Is there a defined incident classification and severity scheme?', phase: 'Classification', weight: 9 },
      { id: 'dt3', text: 'Can you correlate events across multiple data sources?', phase: 'Analysis', weight: 9 },
      { id: 'dt4', text: 'Do you have threat intelligence feeds informing detection?', phase: 'Intelligence', weight: 8 },
      { id: 'dt5', text: 'Are there defined criteria for escalating potential incidents?', phase: 'Escalation', weight: 9 },
      { id: 'dt6', text: 'Can you identify the scope and impact of incidents quickly?', phase: 'Assessment', weight: 9 },
      { id: 'dt7', text: 'Is evidence preserved for later analysis and legal proceedings?', phase: 'Forensics', weight: 8 },
    ],
  },
  {
    id: 'containment',
    title: 'Containment',
    description: 'Limiting the impact and spread of incidents',
    questions: [
      { id: 'cn1', text: 'Are there predefined containment strategies for common incidents?', phase: 'Strategy', weight: 10 },
      { id: 'cn2', text: 'Can you isolate affected systems quickly?', phase: 'Isolation', weight: 10 },
      { id: 'cn3', text: 'Are there procedures for short-term vs long-term containment?', phase: 'Procedures', weight: 8 },
      { id: 'cn4', text: 'Can you block malicious IPs, domains, or accounts rapidly?', phase: 'Blocking', weight: 9 },
      { id: 'cn5', text: 'Are backup systems available for critical functions during containment?', phase: 'Continuity', weight: 8 },
      { id: 'cn6', text: 'Is there a decision framework for containment actions?', phase: 'Decisions', weight: 7 },
    ],
  },
  {
    id: 'eradication',
    title: 'Eradication & Recovery',
    description: 'Removing threats and restoring normal operations',
    questions: [
      { id: 'er1', text: 'Can you identify and remove all components of an attack?', phase: 'Removal', weight: 10 },
      { id: 'er2', text: 'Are there verified clean system images for restoration?', phase: 'Images', weight: 9 },
      { id: 'er3', text: 'Can systems be rebuilt from known-good backups?', phase: 'Restoration', weight: 10 },
      { id: 'er4', text: 'Is there a process for validating systems before returning to production?', phase: 'Validation', weight: 9 },
      { id: 'er5', text: 'Can you reset compromised credentials across the organization?', phase: 'Credentials', weight: 9 },
      { id: 'er6', text: 'Are there defined criteria for declaring an incident resolved?', phase: 'Closure', weight: 8 },
    ],
  },
  {
    id: 'communication',
    title: 'Communication & Reporting',
    description: 'Stakeholder notification and regulatory reporting',
    questions: [
      { id: 'cm1', text: 'Is there a communication plan for internal stakeholders?', phase: 'Internal', weight: 9 },
      { id: 'cm2', text: 'Is there a communication plan for external parties (customers, partners)?', phase: 'External', weight: 9 },
      { id: 'cm3', text: 'Do you know regulatory notification requirements (timeframes, authorities)?', phase: 'Regulatory', weight: 10 },
      { id: 'cm4', text: 'Is there a process for media and public communication?', phase: 'Media', weight: 8 },
      { id: 'cm5', text: 'Are there executive briefing templates and procedures?', phase: 'Executive', weight: 8 },
      { id: 'cm6', text: 'Can you provide real-time status updates during incidents?', phase: 'Updates', weight: 7 },
    ],
  },
  {
    id: 'post-incident',
    title: 'Post-Incident Activities',
    description: 'Learning and improving from incidents',
    questions: [
      { id: 'pi1', text: 'Do you conduct post-incident reviews (lessons learned)?', phase: 'Review', weight: 10 },
      { id: 'pi2', text: 'Are incident reports documented with root cause analysis?', phase: 'Documentation', weight: 9 },
      { id: 'pi3', text: 'Are improvements from reviews tracked and implemented?', phase: 'Improvement', weight: 9 },
      { id: 'pi4', text: 'Is incident data used to improve detection capabilities?', phase: 'Detection', weight: 8 },
      { id: 'pi5', text: 'Are metrics tracked (MTTD, MTTR, incident counts)?', phase: 'Metrics', weight: 8 },
      { id: 'pi6', text: 'Is there a knowledge base of past incidents and responses?', phase: 'Knowledge', weight: 7 },
    ],
  },
  {
    id: 'testing',
    title: 'Testing & Exercises',
    description: 'Validating IR capabilities through practice',
    questions: [
      { id: 'te1', text: 'Are tabletop exercises conducted at least annually?', phase: 'Tabletop', weight: 9 },
      { id: 'te2', text: 'Do exercises include realistic attack scenarios?', phase: 'Scenarios', weight: 8 },
      { id: 'te3', text: 'Is executive participation included in exercises?', phase: 'Leadership', weight: 8 },
      { id: 'te4', text: 'Are technical response procedures tested?', phase: 'Technical', weight: 9 },
      { id: 'te5', text: 'Do you conduct red team or purple team exercises?', phase: 'Red Team', weight: 7 },
      { id: 'te6', text: 'Are exercise findings used to update the IR plan?', phase: 'Updates', weight: 8 },
    ],
  },
  {
    id: 'specific-scenarios',
    title: 'Specific Scenario Playbooks',
    description: 'Prepared responses for common incident types',
    questions: [
      { id: 'ss1', text: 'Do you have a playbook for ransomware incidents?', phase: 'Ransomware', weight: 10 },
      { id: 'ss2', text: 'Do you have a playbook for data breaches?', phase: 'Data Breach', weight: 10 },
      { id: 'ss3', text: 'Do you have a playbook for business email compromise?', phase: 'BEC', weight: 9 },
      { id: 'ss4', text: 'Do you have a playbook for DDoS attacks?', phase: 'DDoS', weight: 8 },
      { id: 'ss5', text: 'Do you have a playbook for insider threats?', phase: 'Insider', weight: 8 },
      { id: 'ss6', text: 'Do you have a playbook for supply chain compromises?', phase: 'Supply Chain', weight: 8 },
    ],
  },
];

export default function IncidentResponse() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'incident-response',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { phase: string; question: string; status: string; priority: string }[] = [];

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({
            phase: q.phase,
            question: q.text,
            status: 'Partial',
            priority: q.weight >= 10 ? 'Critical' : q.weight >= 8 ? 'High' : 'Medium',
          });
        } else if (answer === 'no') {
          gaps.push({
            phase: q.phase,
            question: q.text,
            status: 'Gap',
            priority: q.weight >= 10 ? 'Critical' : q.weight >= 8 ? 'High' : 'Medium',
          });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps: gaps.sort((a, b) => {
        const priorityOrder = { Critical: 0, High: 1, Medium: 2 };
        return priorityOrder[a.priority as keyof typeof priorityOrder] - priorityOrder[b.priority as keyof typeof priorityOrder];
      }),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getReadinessLevel = (score: number) => {
    if (score >= 90) return { level: 'Mature', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 75) return { level: 'Established', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (score >= 60) return { level: 'Developing', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 40) return { level: 'Basic', color: 'text-accent-400', bg: 'bg-accent-500/20' };
    return { level: 'Initial', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const readiness = getReadinessLevel(score);

    let md = `# Incident Response Readiness Assessment\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n\n`;
    md += `## Overall Readiness Score\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Maturity Level:** ${readiness.level}\n\n`;

    md += `## Phase Scores\n\n`;
    md += `| IR Phase | Score |\n`;
    md += `|----------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Improvement Areas\n\n`;

      md += `### Critical Priority\n\n`;
      const critical = gaps.filter((g) => g.priority === 'Critical');
      if (critical.length > 0) {
        critical.forEach((gap) => {
          md += `- **${gap.phase}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No critical gaps identified.\n`;
      }

      md += `\n### High Priority\n\n`;
      const high = gaps.filter((g) => g.priority === 'High');
      if (high.length > 0) {
        high.forEach((gap) => {
          md += `- **${gap.phase}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No high priority gaps identified.\n`;
      }
    }

    md += `\n## NIST IR Framework Phases\n\n`;
    md += `1. **Preparation:** Build IR capabilities before incidents\n`;
    md += `2. **Detection & Analysis:** Identify and understand incidents\n`;
    md += `3. **Containment:** Limit incident impact and spread\n`;
    md += `4. **Eradication & Recovery:** Remove threats and restore operations\n`;
    md += `5. **Post-Incident:** Learn and improve from incidents\n`;

    md += `\n## Key IR Metrics\n\n`;
    md += `- **MTTD (Mean Time to Detect):** Average time to discover incidents\n`;
    md += `- **MTTR (Mean Time to Respond):** Average time to contain incidents\n`;
    md += `- **MTTC (Mean Time to Contain):** Average time from detection to containment\n`;
    md += `- **MTTR (Mean Time to Recover):** Average time to full recovery\n`;

    md += `\n## Standards & Frameworks\n\n`;
    md += `- **NIST SP 800-61:** Computer Security Incident Handling Guide\n`;
    md += `- **ISO 27035:** Information security incident management\n`;
    md += `- **SANS Incident Response:** Industry best practices\n`;
    md += `- **FIRST:** Forum of Incident Response and Security Teams\n`;

    md += `\n## Recommended Actions\n\n`;
    if (score < 75) {
      md += `1. Document formal Incident Response Plan\n`;
      md += `2. Establish Incident Response Team with clear roles\n`;
      md += `3. Implement 24/7 monitoring capability\n`;
      md += `4. Create playbooks for common incidents\n`;
      md += `5. Conduct tabletop exercises quarterly\n`;
      md += `6. Establish IR retainer with external support\n`;
    } else {
      md += `1. Continue regular testing and exercises\n`;
      md += `2. Update playbooks based on emerging threats\n`;
      md += `3. Track and improve IR metrics\n`;
      md += `4. Share threat intelligence with peers\n`;
    }

    md += `\n---\n*Assessment conducted using The Art of CTO Incident Response Readiness*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ir-readiness-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const { score, gaps } = calculateScore();
    const readiness = getReadinessLevel(score);

    const sectionScores: Record<string, number> = {};
    sections.forEach(section => {
      sectionScores[section.id] = getSectionScore(section.id);
    });

    const gapsFormatted = gaps.map(gap => ({
      id: gap.phase,
      title: gap.question,
      severity: gap.priority === 'Critical' ? 'critical' as const :
                gap.priority === 'High' ? 'high' as const : 'medium' as const,
      description: `${gap.phase} - ${gap.status}`,
    }));

    const recommendations = sections
      .filter(s => getSectionScore(s.id) < 80)
      .sort((a, b) => getSectionScore(a.id) - getSectionScore(b.id))
      .map(section => ({
        id: section.id,
        title: `Improve ${section.title}`,
        priority: getSectionScore(section.id) < 40 ? 'high' as const :
                  getSectionScore(section.id) < 60 ? 'high' as const : 'medium' as const,
        description: `Current score: ${getSectionScore(section.id)}%. ${section.description}`,
      }));

    const assessment = await saveResult({
      name: `IR Readiness - ${new Date().toLocaleDateString()}`,
      score_overall: score,
      scores: sectionScores,
      answers,
      gaps: gapsFormatted,
      recommendations,
      metadata: {
        readinessLevel: readiness.level,
        totalQuestions: sections.reduce((sum, s) => sum + s.questions.length, 0),
        answeredQuestions: Object.keys(answers).length,
        gapCount: gaps.length,
        criticalGaps: gaps.filter(g => g.priority === 'Critical').length,
        highGaps: gaps.filter(g => g.priority === 'High').length,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              Security Operations
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              Incident Response Readiness
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Assess your organization&apos;s ability to detect, respond to, and recover from
              security incidents. Evaluate your IR plan, team, and capabilities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Why IR Matters</h3>
              <p className="text-foreground-secondary text-sm">
                It&apos;s not if, but when. Effective incident response reduces breach
                impact, recovery time, and costs. The average cost of a data breach
                is $4.45M - preparation pays off.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Key IR Metrics</h3>
              <p className="text-foreground-secondary text-sm">
                Track MTTD (Mean Time to Detect), MTTR (Mean Time to Respond),
                and MTTC (Mean Time to Contain). Faster detection and response
                significantly reduces breach impact.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">NIST IR Framework</h3>
            <div className="space-y-3 text-sm text-foreground-secondary">
              <div className="flex gap-3">
                <span className="text-accent font-bold w-32">Preparation</span>
                <span>Build capabilities before incidents occur</span>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold w-32">Detection</span>
                <span>Identify and analyze security events</span>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold w-32">Containment</span>
                <span>Limit the impact and spread of incidents</span>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold w-32">Eradication</span>
                <span>Remove threats and restore systems</span>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold w-32">Recovery</span>
                <span>Return to normal operations safely</span>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold w-32">Lessons Learned</span>
                <span>Improve from every incident</span>
              </div>
            </div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6 mb-8">
            <h3 className="text-accent font-semibold mb-2">Common Incident Types</h3>
            <div className="grid md:grid-cols-3 gap-2 text-sm text-foreground-secondary">
              <span>Ransomware</span>
              <span>Data Breach</span>
              <span>Phishing/BEC</span>
              <span>DDoS Attack</span>
              <span>Insider Threat</span>
              <span>Supply Chain</span>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start IR Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 15-20 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      {question.phase}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Section
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, gaps } = calculateScore();
  const readiness = getReadinessLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <div className="flex gap-3">
            <button
              onClick={exportToMarkdown}
              className="bg-cream/10 hover:bg-cream/20 text-foreground px-4 py-2 rounded-lg transition-colors"
            >
              Export Report
            </button>
            {commandCenterEnabled && !saved && (
              <button
                onClick={handleSaveToCommandCenter}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save to Command Center'}
              </button>
            )}
            {saved && (
              <div className="bg-green-600/20 text-green-400 px-4 py-2 rounded-lg flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </div>
            )}
            {saveError && (
              <div className="bg-red-600/20 text-red-400 px-4 py-2 rounded-lg text-sm">
                {saveError}
              </div>
            )}
          </div>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            Incident Response Readiness Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${readiness.bg}`}>
            <span className={`text-4xl font-bold ${readiness.color}`}>{score}%</span>
            <p className={`${readiness.color} font-medium mt-1`}>{readiness.level} Maturity</p>
          </div>
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Phase Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionReadiness = getReadinessLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionReadiness.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 90
                          ? 'bg-green-500'
                          : sectionScore >= 75
                          ? 'bg-blue-500'
                          : sectionScore >= 60
                          ? 'bg-yellow-500'
                          : sectionScore >= 40
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Improvement Areas ({gaps.length})
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-accent text-xs font-medium">
                        {gap.phase}
                      </span>
                      <p className="text-foreground text-sm mt-1">{gap.question}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                        gap.priority === 'Critical'
                          ? 'bg-red-500/20 text-red-400'
                          : gap.priority === 'High'
                          ? 'bg-accent-muted text-accent'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {gap.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Standards & Resources</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p><span className="text-accent">NIST SP 800-61:</span> Computer Security Incident Handling Guide</p>
            <p><span className="text-accent">ISO 27035:</span> Information security incident management</p>
            <p><span className="text-accent">SANS:</span> Incident Handler&apos;s Handbook</p>
            <p><span className="text-accent">FIRST:</span> Forum of Incident Response and Security Teams</p>
          </div>
        </div>

        <div className="text-center mt-8">
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
            }}
            className="text-foreground-tertiary hover:text-accent transition-colors"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
